/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
/**
 * Holds data for all the scheduled premium sessions and session content.
 */

import { action, computed, observable } from 'mobx';

import { DateTime } from 'luxon';

import {
  EnhancedPremiumSession,
  PremiumContentWithScheduledSessions,
  PremiumSession,
  PremiumSessionAggregate,
  PremiumSessionContent,
  PremiumSessionEnrollment,
  PremiumSessionEnrollmentState,
  PremiumSessionInterestedState,
  PremiumSessionSlotInfo,
  PremiumSessionState,
  PremiumSessionUserFacingStatus,
  SessionDefinition,
  SessionInstructor,
} from 'types/premiumSession';
import { toSlug } from 'helpers/utils';
import { CatalogEntry } from 'types';
import {
  getPremiumSessions,
  getPremiumSessionContent,
  getPremiumSessionBySessionId,
  markSessionAsComplete,
} from '../services/PremiumSessionService';

import {
  PREMIUM_VT_SESSION_SEATS_RANGE,
  PREMIUM_VT_SESSION_STATES,
} from '../config/constants';
import type { IRootStore } from './IRootStore';

class PremiumSessionsStore {
  @observable scheduledSessionsloaded = false;

  @observable premiumSessions: PremiumSession[] = [];

  @observable premiumSessionsContent: PremiumSessionContent[] = [];

  @observable instructorsDetails = {};

  @observable loaded = false;

  @observable loading = false;

  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  static getMinutesBeforeSessionStarts = (startTime: string) => {
    if (startTime) {
      const now = DateTime.now();
      const start = DateTime.fromISO(startTime);
      const timeDifference = start.diff(now, 'minutes');

      return timeDifference;
    }

    return 0;
  };

  static getCourseMetaFromSessionSlug(sessionSlug: string) {
    const splitSessionSlug = sessionSlug.split('-');
    return splitSessionSlug?.length >= 2
      ? {
          code: splitSessionSlug[0],
          version: splitSessionSlug[1],
          modality: 'vt',
          sessionSlug: splitSessionSlug[2],
        }
      : {};
  }

  /**
   * Gets the next schedulable session for each session definition in a premium session aggregate.
   * Considers sessions in order and ensures that each subsequent session starts after the previous one ends.
   * This is used to support the "Schedule All" feature since we need to know which session to schedule next to avoid conflicts between sessions.
   * @param premiumSessionAggregate The PremiumSessionAggregate that contains the session data.
   * @returns An object mapping session slugs to their next schedulable session.
   */
  static getConsecutiveSessionsForScheduleAll(
    offeringSlug: string,
    sessions: SessionDefinition[],
    sessionsGroupedBySlug: Record<string, PremiumSession[]>,
  ) {
    // Used for Schedule All feature
    // returns ordered sessions, the next one
    // ending after the previous one
    // {'do101-4.5-s01': premvt_session, ...}
    const scheduleAllSessions: Record<string, PremiumSession | undefined> = {};
    let lastClassEndTime: DateTime | undefined;

    sessions.forEach((sessionDef) => {
      const sessionSlug = `${offeringSlug}-${sessionDef.slug}`;
      const scheduledSessions = sessionsGroupedBySlug[sessionSlug]?.sort(
        (session1, session2) =>
          DateTime.fromISO(session1.start_time).toMillis() -
          DateTime.fromISO(session2.start_time).toMillis(),
      );
      const selectedSession = scheduledSessions?.find(
        (scheduledSession) =>
          // Selecting a session which start time is after previous end time
          // and has available seats and scheduled state
          (!lastClassEndTime ||
            DateTime.fromISO(scheduledSession.start_time) > lastClassEndTime) &&
          scheduledSession.seats_available > 0 &&
          scheduledSession.state === PremiumSessionState.Scheduled,
      );

      if (selectedSession) {
        lastClassEndTime = DateTime.fromISO(selectedSession.end_time);
      }
      scheduleAllSessions[sessionSlug] = selectedSession;
    });

    return scheduleAllSessions;
  }

  /**
   * Translates the session aggregate data into a user-friendly status as well as other useful information.
   * @param fullSessionSlug Example: "do370-4.16-s06"
   */
  static getUserFacingStatusForSessionSlot(
    fullSessionSlug: string,
    premiumSessionAggregate: PremiumSessionAggregate,
    sessionsForScheduleAll: Record<string, PremiumSession | undefined>,
    modulesContent: { content: string }[],
    hasInterestInSession: boolean = false,
  ): PremiumSessionSlotInfo {
    const slotSessions =
      premiumSessionAggregate.sessionsGroupedBySlug?.[fullSessionSlug] || []; // all status, sorted by start time ASC

    const nextSchedulableSession = sessionsForScheduleAll[fullSessionSlug];

    // TODO: can loop less times
    const sessionWithEnrollment = slotSessions.find(
      (s) => !!s.activeEnrollment,
    ); // user might enrolled in a session that may not necessarily be the next one
    const anySessionEnrollment = sessionWithEnrollment?.activeEnrollment;
    const isEnrolledInAnySession = !!anySessionEnrollment;
    const attendedSession = slotSessions?.find((s) => s.hasAttendedOnce); // we look at past sessions no mater if ignorePastSessions is true
    const hasAttendedOnce = attendedSession?.hasAttendedOnce ?? false;
    const isInterested =
      hasInterestInSession ||
      slotSessions.some(
        (s) =>
          s.interested &&
          s.interested.state === PremiumSessionInterestedState.Active,
      );

    let currentSessionDocId = nextSchedulableSession?.doc_id;
    let userFacingStatus = PremiumSessionUserFacingStatus.Unavailable;

    if (isEnrolledInAnySession) {
      userFacingStatus = PremiumSessionUserFacingStatus.Enrolled;
      currentSessionDocId = sessionWithEnrollment?.doc_id;
    } else if (hasAttendedOnce) {
      userFacingStatus = PremiumSessionUserFacingStatus.Attended;
      currentSessionDocId = attendedSession?.doc_id;
    } else if (nextSchedulableSession) {
      userFacingStatus = PremiumSessionUserFacingStatus.Schedulable;
    } else if (isInterested) {
      userFacingStatus = PremiumSessionUserFacingStatus.Requested;
    }

    const modulesContentItems = modulesContent.reduce((contentValues, m) => {
      if (m.content && m.content.trim().length > 0) {
        const [, identifier, number] =
          m.content.match(/^([A-Za-z]+)(\d+)$/) || [];

        if (identifier && number) {
          const numberBase10 = parseInt(number, 10);

          return {
            ...contentValues,
            [identifier]: contentValues[identifier]
              ? [...contentValues[identifier], numberBase10]
              : [numberBase10],
          };
        }
      }
      return contentValues;
    }, {} as Record<string, number[]>);

    return {
      anySessionEnrollment,
      attendedSession,
      currentSessionDocId,
      hasAttendedOnce,
      isEnrolledInAnySession,
      isInterested,
      modulesContentItems,
      nextSchedulableSession,
      sessionWithEnrollment,
      slotSessions,
      userFacingStatus,
    };
  }

  /**
   * Builds per-session slot info for a premium session aggregate.
   * Used by dashboard views and premium session UI.
   */
  buildPremiumSessionAggregateInfo(
    premiumSessionAggregate: PremiumSessionAggregate,
  ): {
    extraSessionInfoMap: Map<string, PremiumSessionSlotInfo>;
    sessionsStatusList: Array<{
      slug: string;
      status: PremiumSessionUserFacingStatus;
    }>;
  } {
    const infoMap = new Map<string, PremiumSessionSlotInfo>();
    const statusList: Array<{
      slug: string;
      status: PremiumSessionUserFacingStatus;
    }> = [];

    const sessionsForScheduleAll =
      PremiumSessionsStore.getConsecutiveSessionsForScheduleAll(
        premiumSessionAggregate.content.offering_slug,
        premiumSessionAggregate.content.sessions,
        premiumSessionAggregate.sessionsGroupedBySlug ?? {},
      );

    premiumSessionAggregate.content.sessions.forEach(
      (sessionDefinition: SessionDefinition) => {
        const fullSessionSlug = `${premiumSessionAggregate.content.offering_slug}-${sessionDefinition.slug}`;
        const hasInterestInSession =
          !!this.rootStore.classesStore.getInterestInSession(fullSessionSlug);

        const info = PremiumSessionsStore.getUserFacingStatusForSessionSlot(
          fullSessionSlug,
          premiumSessionAggregate,
          sessionsForScheduleAll,
          sessionDefinition.modules,
          hasInterestInSession,
        );

        statusList.push({
          slug: fullSessionSlug,
          status: info.userFacingStatus,
        });
        infoMap.set(fullSessionSlug, info);
      },
    );

    return { extraSessionInfoMap: infoMap, sessionsStatusList: statusList };
  }

  @action getPremiumSessions = async (force = true) => {
    if (!force) {
      return this.premiumSessions;
    }

    try {
      const premiumSessions = await getPremiumSessions();
      if (premiumSessions) {
        this.premiumSessions = premiumSessions;
      }
    } catch (e) {
      console.error('Error while getting premium sessions', e);
    } finally {
      this.scheduledSessionsloaded = true;
    }

    return this.premiumSessions;
  };

  // eslint-disable-next-line class-methods-use-this
  convertAvailableSeatsToRange(availableSeats: number) {
    if (availableSeats === 0) return PREMIUM_VT_SESSION_SEATS_RANGE.full;

    if (availableSeats <= 5) return PREMIUM_VT_SESSION_SEATS_RANGE.limited;

    if (availableSeats > 5) return PREMIUM_VT_SESSION_SEATS_RANGE.available;

    return availableSeats;
  }

  @computed get scheduledPremiumSessions() {
    return this.premiumSessions?.filter(
      ({ state }) => state === PREMIUM_VT_SESSION_STATES.scheduled,
    );
  }

  // key value pair where key is offering slug and value is array of all session content.
  // how to use : this.groupedPremiumSessionContentByOffering["jb183-7.0"]
  @computed get groupedPremiumSessionContentByOffering() {
    const dict = this.premiumSessionsContent?.reduce((r, a) => {
      r[a.offering_slug] = r[a.offering_slug] || [];
      r[a.offering_slug] = a;
      return r;
    }, Object.create(null));
    return dict;
  }

  // key value pair where key is offering slug and value is array of all sessions.
  // how to use : this.groupedPremiumSessionsByOffering["jb183-7.0"]
  @computed get groupedScheduledSessionsByOffering() {
    const dict = this.premiumSessions?.reduce((r, a) => {
      r[a.offering_slug] = r[a.offering_slug] || [];
      r[a.offering_slug].push(a);
      return r;
    }, Object.create(null));
    return dict;
  }

  @action getScheduledSessionsBySessionSlug = (
    premiumVTSessionSlug,
  ): PremiumSession[] => {
    let offeringSlug = premiumVTSessionSlug.split('-');
    offeringSlug = `${offeringSlug[0]}-${offeringSlug[1]}`;
    return this.groupedScheduledSessionsByOffering[offeringSlug]?.filter(
      (session) => session.premvt_session_slug === premiumVTSessionSlug,
    );
  };

  @action getScheduledSessionBySessionId = (premiumVTSessionId: string) =>
    this.premiumSessions?.find(
      (session) => session.doc_id === premiumVTSessionId,
    ) || null;

  @action getSessionSecsDurationBySessionSlug = (
    premiumVTSessionSlug: string,
  ) => {
    const sessionExample =
      this.getScheduledSessionsBySessionSlug(premiumVTSessionSlug);

    return sessionExample?.length > 0
      ? // eslint-disable-next-line camelcase
        sessionExample[0]?.duration_secs
      : undefined;
  };

  /**
   * @deprecated The method should not be used. It is only used on Legacy Home page for ClassesWidget
   */
  @computed
  get premiumContentWithScheduledSessions(): PremiumContentWithScheduledSessions[] {
    // TODO: Delete this computed value when the Legacy home page is gone.
    return (
      this.premiumSessionsContent?.map((content) => ({
        ...content,
        scheduledSessions:
          this.groupedScheduledSessionsByOffering[content.offering_slug] || [],
      })) || []
    );
  }

  @computed get isLoadingAggregateList(): boolean {
    return (
      this.rootStore.classesStore.isFetchingPremvtEnrollments ||
      !this.scheduledSessionsloaded ||
      !this.loaded
    );
  }

  /**
   * Gets the first active enrollment found in the list.
   * Enrollment is considered 'active' when state is enrolled or rescheduled.
   * @param enrollments List of enrollments
   */
  getActiveEnrollment(enrollments: PremiumSessionEnrollment[]) {
    return enrollments.find(
      (e) =>
        e.state === PremiumSessionEnrollmentState.Enrolled ||
        e.state === PremiumSessionEnrollmentState.Rescheduled,
    );
  }

  /**
   * Returns true if at least one enrollment in the list is completed.
   * @param enrollments List of enrollments
   */
  hasAttendedOnce(enrollments: PremiumSessionEnrollment[]) {
    return enrollments.some(
      (e) => e.state === PremiumSessionEnrollmentState.Completed,
    );
  }

  /**
   * Sorts EnhancedPremiumSession by start time (asc).
   */
  sortSessionsByStartTime(
    a: EnhancedPremiumSession,
    b: EnhancedPremiumSession,
  ) {
    const aStart = a.start_time ? new Date(a.start_time).getTime() : 0;
    const bStart = b.start_time ? new Date(b.start_time).getTime() : 0;
    return aStart - bStart;
  }

  /**
   * Easy to access Aggregate data via PremiumSessionContent.doc_id (eg "ad364-7.9").
   */
  @computed get premiumSessionAggregateMap(): Map<
    string,
    PremiumSessionAggregate
  > {
    const { classesStore, catalogStore } = this.rootStore;

    // Avoid computing if necessary data is not loaded yet
    const isPremvtReadOnly = this.rootStore.featuresStore.isFeatureEnabled(
      'enablements',
      'show_premvt_schedule_disabled',
    );

    // Ensure all required data is loaded before proceeding
    if (
      !this.rootStore.featuresStore.allFeaturesFetched ||
      !this.scheduledSessionsloaded ||
      !this.loaded ||
      (!isPremvtReadOnly && !classesStore.didFetchPremvtEnrollments) // enrollments dont matter for read only premvt
    ) {
      return new Map();
    }

    // Pre-process data into lookup maps:
    const catalogMap = (catalogStore.allCatalogEntries ?? []).reduce(
      (map, entry) => {
        map.set(toSlug(entry.code, entry.version), entry);
        return map;
      },
      new Map<string, CatalogEntry>(),
    );

    const enrollmentMap = (classesStore.premiumVTEnrollments ?? []).reduce(
      (map, e) => {
        if (!map.has(e.premvt_session_uuid)) {
          map.set(e.premvt_session_uuid, []);
        }
        map.get(e.premvt_session_uuid)!.push(e);
        return map;
      },
      new Map<string, PremiumSessionEnrollment[]>(),
    );

    const sessionsByContentMap = (this.premiumSessions ?? []).reduce(
      (map, s) => {
        if (!map.has(s.offering_slug)) {
          map.set(s.offering_slug, []);
        }
        map.get(s.offering_slug)!.push(s);
        return map;
      },
      new Map<string, PremiumSession[]>(),
    );

    const subscriptionEndTime = DateTime.fromISO(
      this.rootStore.userStore.subscriptionEndTime,
    );

    // 3. Aggregate content to build the final map
    return (this.premiumSessionsContent ?? []).reduce((map, content) => {
      const catalogEntry = catalogMap.get(content.offering_slug);

      const relatedSessions: EnhancedPremiumSession[] = (
        sessionsByContentMap.get(content.doc_id) ?? []
      )
        .map((session) => {
          // utility attributes
          const enrollments = enrollmentMap.get(session.doc_id) ?? [];
          const activeEnrollment = this.getActiveEnrollment(enrollments);
          const attendedOnce = this.hasAttendedOnce(enrollments);
          const interested = classesStore.getInterestInSession(
            session.premvt_session_slug,
          );

          let endsAfterSubscriptionEnd: boolean;

          if (subscriptionEndTime?.isValid) {
            endsAfterSubscriptionEnd =
              subscriptionEndTime < DateTime.fromISO(session.end_time);
          }

          return {
            ...session,
            enrollments,
            interested,
            activeEnrollment,
            hasAttendedOnce: attendedOnce,
            endsAfterSubscriptionEnd,
          };
        })
        .sort(this.sortSessionsByStartTime);

      const sessionsGroupedBySlug = relatedSessions.reduce<
        Record<string, EnhancedPremiumSession[]>
      >((acc, session) => {
        (acc[session.premvt_session_slug] ??= []).push(session);
        return acc;
      }, {});

      const aggregate: PremiumSessionAggregate = {
        catalogEntry,
        content,
        sessions: relatedSessions,
        sessionsGroupedBySlug,
      };

      map.set(aggregate.content.doc_id, aggregate);
      return map;
    }, new Map<string, PremiumSessionAggregate>());
  }

  /**
   * Same as premiumSessionAggregateMap, but as an array.
   */
  @computed get premiumSessionAggregateList(): PremiumSessionAggregate[] {
    return Array.from(this.premiumSessionAggregateMap.values()); // fairly cheap O(n)
  }

  @action getPremiumSessionContent = async (force = true) => {
    if (!force) {
      return this.premiumSessionsContent;
    }
    try {
      this.loading = true;
      const sessionContentState = 'active';
      const premiumSessionsContent = await getPremiumSessionContent(
        sessionContentState,
      );

      if (premiumSessionsContent) {
        this.premiumSessionsContent = premiumSessionsContent;
      }
      if (!this.scheduledSessionsloaded) {
        await this.getPremiumSessions();
      }
    } catch (e) {
      console.error('Error while getting session content', e);
    } finally {
      this.loading = false;
      this.loaded = true;
      // eslint-disable-next-line no-unsafe-finally
      return this.premiumSessionsContent;
    }
  };

  @action fetchPremiumSessionBySessionId = async (premiumSessionId: string) => {
    try {
      const premiumSession = await getPremiumSessionBySessionId(
        premiumSessionId,
      );
      return premiumSession;
    } catch (e) {
      console.error('Error while getting premium session by id', e);
      return {};
    }
  };

  @action markSessionAsComplete = async (
    premiumVTSessionId: string,
    isPremiumSubscriber: boolean,
  ) => {
    let response = {};

    try {
      response = await markSessionAsComplete(premiumVTSessionId);

      if (isPremiumSubscriber) {
        await this.getPremiumSessions();
      }
      return response;
    } catch (e) {
      throw e;
    }
  };

  @action getDetailsForInstructor(instructor: SessionInstructor) {
    const details = this.instructorsDetails[instructor?.username];

    return details ? { ...instructor, ...details } : instructor;
  }

  @action sessionSlugHasScheduledSessions(sessionSlug: string) {
    const scheduledSessions = this.getScheduledSessionsBySessionSlug(
      sessionSlug,
    )?.filter(
      (session) =>
        session.state === PREMIUM_VT_SESSION_STATES.scheduled &&
        session.seats_available > 0,
    );

    return scheduledSessions?.length > 0;
  }
}

export default PremiumSessionsStore;
